import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileSearch, Hash, UserCheck, Database, Wallet, ArrowRight } from "lucide-react";

const HowItWorks = () => {
  // Per the brand content spec: Capture → Code → Approve → Post → Pay.
  const steps = [
    {
      icon: <FileSearch className="w-10 h-10 text-accent" />,
      title: "Capture",
      description: "AI-powered data extraction on PDF, email-attached, and EDI invoices. Line-item parsing for PO-based invoices; header-only where that's all that's available. Vendor match against your ERP master on first ingestion.",
    },
    {
      icon: <Hash className="w-10 h-10 text-accent" />,
      title: "Code",
      description: "GL account, cost center, project/WBS, and property or entity coding applied from supplier defaults, PO context, or a learned pattern from historical postings. Exceptions surface for a human; everything else codes without human touch.",
    },
    {
      icon: <UserCheck className="w-10 h-10 text-accent" />,
      title: "Approve",
      description: "Role-based and amount-based routing. Per-supplier approver rules — approvers auto-assigned by supplier, so routing doesn't need to happen by hand. Mobile-friendly approval. No ERP license required for approvers.",
    },
    {
      icon: <Database className="w-10 h-10 text-accent" />,
      title: "Post",
      description: "Native write-back into your ERP using its own API or integration layer. Invoices, intercompany records, payment data, and linked images flow through automatically. No reconciliation lag between EZ Cloud and the ERP.",
    },
    {
      icon: <Wallet className="w-10 h-10 text-accent" />,
      title: "Pay",
      description: "ACH, check, or card. Payment data syncs back to the ERP as part of the same integration. Supplier self-service portal for payment status.",
    },
  ];

  return (
    <>
      {/* What EZ Cloud does — 5-step flow */}
      <section id="how-it-works" className="py-20 bg-white scroll-mt-32">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              What EZ Cloud does
            </h2>
            <p className="text-xl text-muted-foreground">
              Five stages from inbox to payment, all posting directly into your ERP.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <Card key={index} className="border border-gray-200 shadow-sm hover:shadow-medium transition-spring bg-white relative h-full flex flex-col">
                <div className="absolute -top-5 left-6">
                  <div className="w-10 h-10 bg-primary-gradient rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {index + 1}
                  </div>
                </div>
                <CardHeader className="pt-10 flex-shrink-0">
                  <div className="mb-3">{step.icon}</div>
                  <CardTitle className="text-xl font-semibold text-foreground">
                    {step.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {step.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Proof — HACOV results block */}
      <section className="bg-hero-gradient text-white relative overflow-hidden py-20">
        <div className="absolute inset-0 bg-primary/10"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl -translate-x-48 -translate-y-48"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl translate-x-48 translate-y-48"></div>

        <div className="container mx-auto px-4 relative">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4">Customer results</h2>
            <p className="text-lg text-white/80">
              Quantified results from a live Yardi customer — plus how we run on Oracle Fusion and EBS.
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 max-w-4xl mx-auto">
            <div className="flex items-center gap-2 text-accent text-sm font-semibold uppercase tracking-wide mb-4">
              Housing Authority of Covington · Yardi Voyager · live since 2021
            </div>
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div>
                <div className="text-3xl font-bold text-accent mb-1">7 → 2 days</div>
                <div className="text-white/80 text-sm">Invoice processing (71% reduction)</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-accent mb-1">80%</div>
                <div className="text-white/80 text-sm">Reduction in invoice errors</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-accent mb-1">~$79K</div>
                <div className="text-white/80 text-sm">Annual labor savings</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-accent mb-1">3 → 25+</div>
                <div className="text-white/80 text-sm">Active users</div>
              </div>
            </div>
            <blockquote className="text-white/90 italic border-l-2 border-accent pl-6 py-2">
              "EZ Cloud has made my life in a busy accounts payable environment more efficient. The whole team was skeptical at first about how EZ Cloud and Yardi were going to work together, but they have — and it has been seamless. The team loves not having to physically sign approval on each invoice. Now they receive a notification, click the link, review, and approve. It's easy."
              <footer className="text-sm text-white/70 mt-3 not-italic">
                — Denise Steffen, Staff Accountant at Housing Authority of Covington
              </footer>
            </blockquote>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                size="lg"
                variant="secondary"
                className="bg-accent text-accent-foreground hover:bg-accent/90"
                asChild
              >
                <Link to="/customers/hacov">
                  Read the full case study
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button size="lg" variant="outline-on-dark" asChild>
                <a href="/case-studies/EZ-Cloud-HACOV-Case-Study.pdf" download target="_blank">
                  Download PDF
                </a>
              </Button>
            </div>
          </div>

          {/* More customer stories — the Oracle ICP deployments */}
          <div className="max-w-4xl mx-auto mt-10">
            <p className="text-center text-white/70 text-sm mb-5">More customer stories</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Link
                to="/customers/health-system-oracle-fusion"
                className="group bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl p-5 transition-all"
              >
                <div className="text-accent text-xs font-semibold uppercase tracking-wide mb-2">
                  Healthcare · Oracle Fusion
                </div>
                <div className="flex items-center justify-between text-white font-semibold mb-1">
                  A major Australian health service
                  <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-accent group-hover:translate-x-0.5 transition-all flex-shrink-0 ml-2" />
                </div>
                <p className="text-white/70 text-sm">
                  AI email exception prevention on Oracle Fusion — 2,500+ supplier emails processed with zero errors in a proof of concept.
                </p>
              </Link>
              <Link
                to="/customers/school-district-oracle-ebs"
                className="group bg-white/10 hover:bg-white/15 border border-white/20 rounded-xl p-5 transition-all"
              >
                <div className="text-accent text-xs font-semibold uppercase tracking-wide mb-2">
                  K-12 · Oracle EBS
                </div>
                <div className="flex items-center justify-between text-white font-semibold mb-1">
                  A large US public school district
                  <ArrowRight className="w-4 h-4 text-white/40 group-hover:text-accent group-hover:translate-x-0.5 transition-all flex-shrink-0 ml-2" />
                </div>
                <p className="text-white/70 text-sm">
                  Live on Oracle EBS across ~25 schools and departments — fund accounting, district tax, and in-workflow receiving.
                </p>
              </Link>
            </div>
            <div className="text-center mt-6">
              <Link to="/customers" className="text-accent hover:underline text-sm font-medium">
                See all customer stories →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HowItWorks;